
const baseUnit = 4;

enum Radius {
    Large = baseUnit * 4,
    Medium = baseUnit * 2.6,
    Small = baseUnit,
}

enum Stroke {
    Large = baseUnit * 1.5,
    Medium = baseUnit,
    Small = baseUnit * 0.5,
}

enum Color {
    Light = '#EEEEEE',
    Dark = '#222222',
    Accent = '#5F86E8',
}

/**
 * For use with the `layout.text-size` property.
 * Sets the text size in pixels.
 */
const textSize = [
    "step",
    ["zoom"],
    0,
    12, baseUnit * 4,
];

export const layers = {
    waypoint: {
        active: ['waypoints-active.hot', 'waypoints-active.cold'],
        inactive: ['waypoints-inactive.hot', 'waypoints-inactive.cold'],
        hover: ['waypoints-route-hover.hot', 'waypoints-route-hover.cold'],
    },
    route: ['route-outline-transparent.hot', 'route-outline-transparent.cold'],
};

export const MAP_EDITOR_LAYERS = ["storyClickLayer", ...layers.route, ...layers.waypoint.active, ...layers.waypoint.inactive, ...layers.waypoint.hover]

/**
 * When instantiating Mapbox Draw, [an array of objects can be passed to the `styles` member](https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md#options).
 * The objects contained in the array should conform to the [Mapbox Style Specification](https://docs.mapbox.com/mapbox-gl-js/style-spec/),
 * but the `source` property should not be included. Mapbox Draw provides a source automatically.
 * All unique objects (in terms of their `id` property) will automatically be added as layers in Mapbox (ordered by their position in the array).
 * See the section in the API documentation regarding [styling](https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md#styling-draw) for additional information.
 */
export const styles: object[] = [
    // ROUTE, OUTLINE, TRANSPARENT
    // This is a wide, transparent outline enabling easier mouse and touch interaction with the LineString
    {
        id: "route-outline-transparent",
        type: "line",
        filter: [
            "all",
            ["==", "$type", "LineString"],
            // ! The properties of GeoJSON which is rendered in `toDisplayFeatures()` of a custom mode for `MapboxDraw` – without being added to Draw's data store – is not prefixed with `user_`
            ["!=", "meta", "reroutingIndicator"],
        ],
        paint: {
            "line-color": "transparent",
            "line-width": [
                "interpolate", ["linear"], ["zoom"],
                4, 0,
                10, Stroke.Medium * 2,
                15, Stroke.Large * 2,
            ],
        },
    },
    // ROUTE, OUTLINE
    {
        id: "route-outline",
        type: "line",
        filter: [
            "all",
            ["==", "$type", "LineString"],
            ["!=", "meta", "reroutingIndicator"],
        ],
        layout: {
            "line-cap": "round",
            "line-join": "round",
        },
        paint: {
            "line-color": Color.Light,
            "line-width": [
                "interpolate", ["linear"], ["zoom"],
                4, 0,
                10, Stroke.Medium,
                15, Stroke.Large,
            ],
        },
    },
    // ROUTE, LINE
    {
        id: "route-line",
        type: "line",
        filter: [
            "all",
            ["==", "$type", "LineString"],
            ["!=", "meta", "reroutingIndicator"],
        ],
        layout: {
            "line-cap": "round",
            "line-join": "round",
        },
        paint: {
            "line-color": Color.Accent,
            "line-width": [
                "interpolate", ["linear"], ["zoom"],
                4, 0,
                10, Stroke.Small,
                15, Stroke.Medium,
            ],
        },
    },
    // REROUTING INDICATOR
    {
        id: "rerouting-indicator",
        type: "line",
        filter: [
            "all",
            ["==", "$type", "LineString"],
            ["==", "meta", "reroutingIndicator"],
        ],
        layout: {
            "line-cap": "round",
            "line-join": "round",
        },
        paint: {
            "line-color": Color.Accent,
            "line-width": [
                "interpolate", ["linear"], ["zoom"],
                4, 0,
                10, Stroke.Small,
                15, Stroke.Medium,
            ],
            "line-dasharray": [0, 2],
        },
    },
    // WAYPOINTS DISPLAYED ON ROUTE HOVER
    {
        id: "waypoints-route-hover",
        type: "circle",
        // ! The properties of GeoJSON which is rendered in `toDisplayFeatures()` of a custom mode for `MapboxDraw` – without being added to Draw's data store – is not prefixed with `user_`
        filter: ["==", "meta", "waypoint"],
        paint: {
            "circle-radius": Radius.Medium,
            "circle-color": Color.Light,
            "circle-stroke-color": Color.Accent,
            "circle-stroke-width": baseUnit / 2,
        },
    },
    // WAYPOINTS, ACTIVE
    {
        id: "waypoints-active",
        type: "circle",
        filter: [
            "all",
            ["==", "user_meta", "waypoint"],
            ["==", "active", "true"],
        ],
        paint: {
            "circle-radius": Radius.Medium,
            "circle-color": Color.Light,
            "circle-stroke-color": Color.Accent,
            "circle-stroke-width": baseUnit / 2,
        },
    },
    // WAYPOINTS, INACTIVE
    {
        id: "waypoints-inactive",
        type: "circle",
        filter: [
            "all",
            ["==", "user_meta", "waypoint"],
            ["==", "active", "false"],
        ],
        paint: {
            "circle-radius": Radius.Small,
            "circle-color": Color.Light,
            "circle-stroke-color": Color.Accent,
            "circle-stroke-width": baseUnit / 2,
        },
    },
    // STORIES, INACTIVE
    {
        id: "stories-inactive",
        type: "circle",
        filter: [
            "all",
            // ! When `MapboxDraw` is instantiated with the `userProperties` property set to `true`, properties of a feature added by a user will also be available for style queries and prefixed with `user_`
            ["==", "user_meta", "story"],
            ["!=", "user_meta", "hidden"],
            ["==", "active", "false"],
        ],
        paint: {
            "circle-radius": Radius.Large,
            "circle-color": Color.Dark,
            "circle-stroke-color": Color.Light,
            "circle-stroke-width": baseUnit / 2,
        },
    },
    // STORIES, INACTIVE, ORDER
    {
        id: "stories-inactive-order",
        type: "symbol",
        filter: [
            "all",
            ["==", "user_meta", "story"],
            ["!=", "user_meta", "hidden"],
            ["==", "active", "false"],
        ],
        layout: {
            "text-field": ["get", "user_order"],
            "text-size": textSize,
            'text-justify': 'center',
            "text-allow-overlap": true,
            "symbol-sort-key": ["get", "user_order"],
        },
        paint: {
            "text-color": Color.Light,
            "text-halo-color": Color.Dark,
            // Max text halo width is 1/4 of the text-size
            "text-halo-width": [
                "step",
                ["zoom"],
                0,
                12, baseUnit,
            ],
            "text-halo-blur": Radius.Large,
        },
    },
    // STORIES, ACTIVE
    {
        id: "stories-active",
        type: "circle",
        filter: [
            "all",
            ["==", "user_meta", "story"],
            ["!=", "user_meta", "hidden"],
            ["==", "active", "true"],
        ],
        paint: {
            "circle-radius": Radius.Large,
            "circle-color": Color.Accent,
            "circle-stroke-color": Color.Light,
            "circle-stroke-width": baseUnit / 2,
        },
    },
    // STORIES, ACTIVE, ORDER
    {
        id: "stories-active-order",
        type: "symbol",
        filter: [
            "all",
            ["==", "user_meta", "story"],
            ["!=", "user_meta", "hidden"],
            ["==", "active", "true"],
        ],
        layout: {
            // `editor-story-active` icon is added in the `MotesExpressIcons` class
            "text-field": ["get", "user_order"],
            "text-size": textSize,
            'text-justify': 'center',
            "text-allow-overlap": true,
            "symbol-sort-key": ["get", "user_order"],
        },
        paint: {
            "text-color": Color.Light,
        },
    },
    // MARKER (used for highlighting specific positions, e.g. the position of a potential new story)
    {
        id: "marker",
        type: "symbol",
        filter: [
            "all",
            ["==", "user_meta", "marker"],
            ["!=", "user_meta", "hidden"]
        ],
        layout: {
            "icon-image": "default_pin_icon_blue",
            "icon-size": 0.38,
            "icon-anchor": "bottom",
            "icon-allow-overlap": true
        }
    },
];
